import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { blogAPI, newsletterAPI } from '../services/api';

// Newsletter Form Component (reused from Blog page)
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await newsletterAPI.subscribe(email);
      setStatus('success');
      setMessage('Successfully subscribed! Check your inbox.');
      setEmail('');
      setTimeout(() => setStatus(null), 5000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-4">
        <input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D234E]"
        />
        <button
          type="submit"
          className="bg-[#0D234E] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#1a3a6e] transition-colors"
        >
          Subscribe
        </button>
      </form>
      {status === 'success' && (
        <div className="text-green-600 font-medium">{message}</div>
      )}
      {status === 'error' && (
        <div className="text-red-600 font-medium">{message}</div>
      )}
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const postData = await blogAPI.getPost(slug);
      setPost(postData);
      setError(null);
    } catch (err) {
      setError('Failed to load blog post. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D234E]"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="text-xl text-red-600 mb-4">{error || 'Post not found'}</p>
          <Link
            to="/blog"
            className="inline-flex items-center text-[#0D234E] font-semibold hover:text-[#BFB47E] transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D234E] to-[#1a3a6e] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-gray-300 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
          
          <div className="inline-block bg-[#BFB47E] bg-opacity-20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#BFB47E] font-semibold text-sm">{post.category}</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center text-gray-300 gap-6 mb-6">
            <div className="flex items-center space-x-2">
              <User size={18} />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>{formatDate(post.date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={18} />
              <span>{post.readTime}</span>
            </div>
          </div>

          <button
            onClick={handleShare}
            className="inline-flex items-center bg-[#BFB47E] bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-md transition-colors"
          >
            <Share2 size={18} className="mr-2" />
            Share Article
          </button>
        </div>
      </section>

      {/* Featured Image */}
      {post.image && (
        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[400px] object-cover rounded-xl shadow-lg"
            />
          </div>
        </section>
      )}

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-[#0D234E] prose-headings:font-bold
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-[#0D234E] prose-a:no-underline hover:prose-a:text-[#BFB47E]
              prose-strong:text-[#0D234E]
              prose-ul:text-gray-700 prose-ol:text-gray-700
              prose-li:my-2
              prose-code:text-[#0D234E] prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded
            "
            dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>').replace(/## /g, '<h2>').replace(/# /g, '<h1>').replace(/<h2>/g, '<h2 class="text-2xl font-bold mt-8 mb-4">').replace(/<h1>/g, '<h1 class="text-3xl font-bold mt-8 mb-4">') }}
          />
        </div>
      </article>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0D234E] mb-4">
            Stay Updated
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get the latest digital marketing insights delivered to your inbox
          </p>
          <NewsletterForm />
        </div>
      </section>

      {/* Related Articles CTA */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#0D234E] font-semibold hover:text-[#BFB47E] transition-colors text-lg"
          >
            <ArrowLeft size={20} className="mr-2" />
            Read More Articles
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
